import i18n from "i18n";
import HttpRequestError from "errors/HttpRequestError";
import EnvironmentUtils from "utils/EnviromentUtils";
import { plainToClass } from "class-transformer";

import { ErrorCode } from "constants/ErrorCode";
import { store } from "index";
import { logout } from "redux/actions/account";
// class-transformer の「クラスそのもの」を表す型。ライブラリから import しない。
//   0.3.1  ClassType         (class-transformer/ClassTransformer)
//   0.3.2  ClassConstructor  (class-transformer から直接)
// ★パッチ版で名前も置き場所も変わる★。この repo は package-lock.json と yarn.lock を
// 両方 持っていて、前者が 0.3.1・後者が 0.3.2 に解決するため、どちらで入れたかで
// 通ったり落ちたりしていた（2026-07-28 実測。手元 0.3.2 で通り、CI 0.3.1 で落ちた）。
// 中身はどちらも同じ形なので、ここで持つ。型だけなので実行時には残らない。
type ClassType<T> = { new (...args: any[]): T };

const BASE_PATH = EnvironmentUtils.getApiUrl();

export default class RequestUtils {
  private constructor() {}

  static async post<T>(cls: ClassType<T>, path: string, data: any): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "POST", path, data);
  }

  static async get<T>(cls: ClassType<T>, path: string): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "GET", path);
  }

  static async stringList(path: string): Promise<string[]> {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async stringListPost(path: string, data: any): Promise<string[]> {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async count(path: string): Promise<number> {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async countPost(path: string, data: any): Promise<number> {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async put<T>(cls: ClassType<T>, path: string, data: any): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "PUT", path, data);
  }

  static async delete<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T> {
    return await RequestUtils.fetchAndPlainToClass(cls, "DELETE", path, data);
  }

  static async postArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(
      cls,
      "POST",
      path,
      data
    );
  }

  static async getArray<T>(cls: ClassType<T>, path: string): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(cls, "GET", path);
  }

  static async getPlain(path: string) {
    return await RequestUtils.fetchBase("GET", path);
  }

  static async postPlain(path: string, data: any) {
    return await RequestUtils.fetchBase("POST", path, data);
  }

  static async putArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(cls, "PUT", path, data);
  }

  static async deleteArray<T>(
    cls: ClassType<T>,
    path: string,
    data: any
  ): Promise<T[]> {
    return await RequestUtils.fetchAndPlainToClassArray(
      cls,
      "DELETE",
      path,
      data
    );
  }

  static async postVoid(path: string, data: any) {
    await RequestUtils.fetchBase("POST", path, data);
  }

  static async getVoid(path: string) {
    await RequestUtils.fetchBase("GET", path);
  }

  static async putVoid(path: string, data: any) {
    await RequestUtils.fetchBase("PUT", path, data);
  }

  static async deleteVoid(path: string, data: any) {
    await RequestUtils.fetchBase("DELETE", path, data);
  }

  public static async getBlob(url: string, withBasePath?: boolean) {
    const accessToken = store.getState().account?.accessToken;
    const res: Response = await RequestUtils.timeout(
      5 * 60 * 1000,
      fetch(withBasePath ? BASE_PATH + url : url, {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: {
          // IEではGETがキャッシュされてしまうため
          "If-Modified-Since": "Thu, 01 Jun 1970 00:00:00 GMT",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    );
    return await RequestUtils.handleBlobResponse(res);
  }

  private static async timeout(
    ms: number,
    promise: Promise<any>
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => reject(new HttpRequestError(ErrorCode.TIMEOUT, 0)), ms);
      promise.then(resolve, reject);
    });
  }

  private static async fetchAndPlainToClass<T>(
    cls: ClassType<T>,
    method: string,
    path: string,
    data: any = undefined
  ) {
    const json = await RequestUtils.fetchBase(method, path, data);

    return plainToClass(cls, json);
  }

  private static async fetchAndPlainToClassArray<T>(
    cls: ClassType<T>,
    method: string,
    path: string,
    data: any = undefined
  ) {
    const json: unknown[] = await RequestUtils.fetchBase(method, path, data);

    return plainToClass(cls, json);
  }

  private static async fetchBase(
    method: string,
    path: string,
    data: any = undefined,
    isFormDate = false
  ) {
    try {
      const accessToken = store.getState().account?.accessToken;
      const res: Response = await RequestUtils.timeout(
        1 * 60 * 1000,
        fetch(BASE_PATH + path, {
          method: method,
          mode: "cors",
          credentials: "include",
          headers: isFormDate
            ? undefined
            : {
                "Content-Type": "application/json; charset=utf-8",
                // IEではGETがキャッシュされてしまうため
                "If-Modified-Since": "Thu, 01 Jun 1970 00:00:00 GMT",
                Authorization: `Bearer ${accessToken}`,
              },
          body: data ? JSON.stringify(data) : data,
        })
      );
      return await RequestUtils.handleJsonResponse(res);
    } catch (error) {
      RequestUtils.alertForError(error);
      // @ts-ignore
      if (error.statusCode === 401) {
        // @ts-ignore
        store.dispatch(logout());
      }
      throw error;
    }
  }

  public static async postFormData<T>(
    cls: ClassType<T>,
    path: string,
    data: FormData
  ) {
    const res: Response = await RequestUtils.timeout(
      1 * 60 * 1000,
      fetch(BASE_PATH + path, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: data,
      })
    );
    const json = await RequestUtils.handleJsonResponse(res);
    return plainToClass(cls, json);
  }

  public static async postFormDataMultipart<T>(
    cls: ClassType<T>,
    path: string,
    formData: FormData
  ): Promise<T> {
    const res: Response = await RequestUtils.timeout(
      1 * 60 * 1000,
      fetch(BASE_PATH + path, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: undefined,
        body: formData,
      })
    );
    const json = await RequestUtils.handleJsonResponse(res);
    return plainToClass(cls, json);
  }

  /**
   * ★止められている間、同じ知らせを 何度も 出さない★
   *
   * 一つの画面は API を いくつも 並べて 叩く。失敗すると その数だけ alert が出る。
   * 2026-08-13 実測（QA・未払いで止めた状態）……
   *   TOP を開く   → 8 回、/payment を開く → 3 回。閉じても閉じても また出る。
   *   ★払う画面へ 行く前に OK を 連打させられる★＝ 案内した道が また 塞がる。
   *
   * 402（未払いで停止）は ★状態★ であって 出来事ではない。何度 伝えても 増える
   * 情報が 無いので、ひと塊に まとめる。他の status は 今までどおり 毎回 出す
   * （そちらは 一回ごとに 別の 出来事で、まとめると 取りこぼす）。
   */
  /**
   * ★止まっている間は 覆い隠さず、お支払い画面へ 連れて行く★
   *
   * 代表・経営で 2026-08-13 に 決まった 形（案三）。
   *   一 どの画面を 開いても お支払い画面へ 送る
   *   二 理由は 入り直した後 一度だけ 出す（無言で 飛ぶと「壊れた」と 読まれる）
   *
   * ★なぜ ダイアログを 都度 出さないか★
   *   画面ひとつが API を 何本も 並べて 叩くので、失敗した数だけ 出る。
   *   2026-08-13 の 実測 …… TOP で 8 回、/payment で 3 回。
   *   閉じても 閉じても また出て、案内した「設定 → お支払い」へ 辿り着けなかった。
   *
   * ★なぜ 覆い隠さないか★
   *   覆う実装は 穴が空きやすい（重なり順・スクロール・別のモーダルが 上に出る）。
   *   連れて行けば 判定は route 一箇所で 済み、覆い漏れという 概念が 消える。
   */
  private static readonly PAY_PATH = "/payment";
  private static readonly SUSPEND_EXPLAINED_KEY = "billingSuspendedExplained";

  private static alertForError(error: any): void {
    if (error?.statusCode === 402) {
      RequestUtils.goToPaymentPage(error?.message);
      return;
    }
    alert(`${error?.message}`);
  }

  /** 入り直したら また 一度だけ 理由を 出す（ログイン成功時に 呼ぶ）。 */
  public static resetBillingSuspendedNotice(): void {
    try {
      window.sessionStorage.removeItem(RequestUtils.SUSPEND_EXPLAINED_KEY);
    } catch (_e) {
      // プライベート閲覧などで 使えない事が ある。出せないだけで 支障は 無い。
    }
  }

  private static goToPaymentPage(message?: string): void {
    let explained = false;
    try {
      explained =
        window.sessionStorage.getItem(RequestUtils.SUSPEND_EXPLAINED_KEY) ===
        "1";
    } catch (_e) {
      explained = false;
    }
    if (!explained) {
      try {
        window.sessionStorage.setItem(RequestUtils.SUSPEND_EXPLAINED_KEY, "1");
      } catch (_e) {
        // 覚えられない時は 毎回 出る。黙って 飛ばすより 良い。
      }
      alert(
        message ||
          i18n.t("payment:suspended.redirect")
      );
    }
    // ★既に お支払い画面に 居るなら 動かさない★
    //   これが 無いと、お支払い画面の 中の どれか 一本が 402 を 返した時に
    //   読み込み → 402 → 読み込み …… と 回り続ける。
    if (window.location.pathname !== RequestUtils.PAY_PATH) {
      // ★連れて来られた事を お支払い画面に 伝える★
      //   お支払い画面は 権限の弱いスタッフを TOP へ 押し返す。押し返された TOP は
      //   402 で また ここへ 来るので、印が 無いと ★永久に 往復する★。
      try {
        window.sessionStorage.setItem(RequestUtils.SUSPEND_REDIRECT_KEY, "1");
      } catch (_e) {
        // 覚えられない時は 押し返しが 残る。往復は 下の 判定で 止める。
      }
      window.location.assign(RequestUtils.PAY_PATH);
    }
  }

  public static readonly SUSPEND_REDIRECT_KEY = "billingSuspendedRedirect";

  /** 止められて お支払い画面へ 連れて来られたか。 */
  public static wasSentToPaymentBySuspension(): boolean {
    try {
      return (
        window.sessionStorage.getItem(RequestUtils.SUSPEND_REDIRECT_KEY) === "1"
      );
    } catch (_e) {
      return false;
    }
  }

  private static async handleJsonResponse(res: Response) {
    if (res.ok) {
      return res.json();
    }
    const data = await res.json();
    throw new HttpRequestError(data.message, res.status);
  }

  private static async handleBlobResponse(res: Response) {
    if (res.ok) {
      return res.blob();
    }
    const data = await res.json();
    throw new HttpRequestError(data.message, res.status);
  }
}
